/**
 * Tool Registry
 *
 * Maps each tool's URL slug to its metadata, including the output type
 * needed by the AggregationView to compute the right kind of aggregate.
 */

export type ToolOutputType = 'classification' | 'ranking' | 'scoring';

export interface ToolDefinition {
  /** URL slug, e.g. 'eisenhower-matrix' */
  type: string;
  name: string;
  outputType: ToolOutputType;
  description: string;
  /** Whether this tool naturally produces a force-ranked list */
  producesRankedList: boolean;
}

export const TOOL_REGISTRY: Record<string, ToolDefinition> = {
  'eisenhower-matrix':       { type: 'eisenhower-matrix',       name: 'Eisenhower Matrix',        outputType: 'classification', producesRankedList: true,  description: 'Categorize by urgency and importance' },
  'rice-scoring':            { type: 'rice-scoring',            name: 'RICE Scoring',             outputType: 'scoring',        producesRankedList: true,  description: 'Reach, Impact, Confidence, Effort' },
  'moscow-method':           { type: 'moscow-method',           name: 'MoSCoW Method',            outputType: 'classification', producesRankedList: false, description: 'Must have, Should have, Could have, Won\'t have' },
  'wsjf':                    { type: 'wsjf',                    name: 'WSJF',                     outputType: 'scoring',        producesRankedList: true,  description: 'Weighted Shortest Job First' },
  'impact-effort-matrix':    { type: 'impact-effort-matrix',    name: 'Impact/Effort Matrix',     outputType: 'classification', producesRankedList: true,  description: 'Categorize by impact and effort' },
  'stack-ranking':           { type: 'stack-ranking',           name: 'Stack Ranking',            outputType: 'ranking',        producesRankedList: true,  description: 'Rank items by priority order' },
  'pairwise-ranking':        { type: 'pairwise-ranking',        name: 'Pairwise Ranking',         outputType: 'ranking',        producesRankedList: true,  description: 'Compare items head-to-head' },
  'dot-voting':              { type: 'dot-voting',              name: 'Dot Voting',               outputType: 'ranking',        producesRankedList: true,  description: 'Allocate votes across items' },
  'buy-a-feature':           { type: 'buy-a-feature',           name: 'Buy a Feature',            outputType: 'ranking',        producesRankedList: true,  description: 'Allocate budget across features' },
  'pareto-analysis':         { type: 'pareto-analysis',         name: 'Pareto Analysis',          outputType: 'classification', producesRankedList: true,  description: 'Find the vital few (80/20)' },
  'cost-of-delay':           { type: 'cost-of-delay',           name: 'Cost of Delay',            outputType: 'scoring',        producesRankedList: true,  description: 'Quantify cost of delaying each item' },
  'speed-boat':              { type: 'speed-boat',              name: 'Speed Boat',               outputType: 'classification', producesRankedList: false, description: 'Identify anchors slowing progress' },
  'prune-product-tree':      { type: 'prune-product-tree',      name: 'Prune the Product Tree',   outputType: 'classification', producesRankedList: false, description: 'Decide what to keep, trim, or grow' },
  'simple-weighted-scoring': { type: 'simple-weighted-scoring', name: 'Weighted Scoring',         outputType: 'scoring',        producesRankedList: true,  description: 'Score items across weighted criteria' },
  'max-priorities-pyramid':  { type: 'max-priorities-pyramid',  name: 'Max Priorities Pyramid',   outputType: 'ranking',        producesRankedList: true,  description: 'Pyramid-based priority allocation' },
  'fast-vs-right':           { type: 'fast-vs-right',           name: 'Fast vs Right',            outputType: 'classification', producesRankedList: false, description: 'Balance speed vs quality tradeoffs' },
  'ahp':                     { type: 'ahp',                     name: 'AHP',                      outputType: 'scoring',        producesRankedList: true,  description: 'Analytic Hierarchy Process' },
  'magic-prioritization':    { type: 'magic-prioritization',    name: 'Magic Prioritization',     outputType: 'ranking',        producesRankedList: true,  description: 'Rapid prioritization across lenses' },
};

export function getToolDef(toolType: string): ToolDefinition | undefined {
  return TOOL_REGISTRY[toolType];
}

export function getOutputType(toolType: string): ToolOutputType {
  return TOOL_REGISTRY[toolType]?.outputType ?? 'ranking';
}

/** Canonical registry ids that map to a different TOOL_REGISTRY slug. */
const REGISTRY_ID_TO_SLUG: Record<string, string> = {
  moscow: 'moscow-method',
  impact_effort_matrix: 'impact-effort-matrix',
  eisenhower_matrix: 'eisenhower-matrix',
  stack_ranking: 'stack-ranking',
  pairwise_ranking: 'pairwise-ranking',
  dot_voting: 'dot-voting',
  buy_a_feature: 'buy-a-feature',
  pareto_analysis: 'pareto-analysis',
  cost_of_delay: 'cost-of-delay',
  speed_boat: 'speed-boat',
  prune_product_tree: 'prune-product-tree',
  simple_weighted_scoring: 'simple-weighted-scoring',
  max_priorities_pyramid: 'max-priorities-pyramid',
  fast_vs_right: 'fast-vs-right',
  magic_prioritization: 'magic-prioritization',
  rice: 'rice-scoring',
};

/**
 * The TOOL_REGISTRY slug for any form of a tool id — registry id
 * (`impact_effort_matrix`), slug (`impact-effort-matrix`) or the underscore /
 * hyphen variant of either. Undefined when no registry entry answers to it.
 */
export function resolveToolRegistrySlug(toolId: string | null | undefined): string | undefined {
  if (!toolId) return undefined;
  const candidates = [
    toolId,
    toolId.replace(/_/g, '-'),
    REGISTRY_ID_TO_SLUG[toolId],
    REGISTRY_ID_TO_SLUG[toolId.replace(/-/g, '_')],
  ];
  return candidates.find((key): key is string => Boolean(key && TOOL_REGISTRY[key]));
}

/** Resolve output type from registry tool id (e.g. moscow, impact_effort_matrix) or URL slug. */
export function getOutputTypeForSessionTool(toolId: string | null | undefined): ToolOutputType {
  const slug = resolveToolRegistrySlug(toolId);
  return slug ? TOOL_REGISTRY[slug].outputType : 'ranking';
}
